import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { ParkingProcessService } from '../../../api/gen/api/parkingProcess.service';
import { ParkingProcessListItemDto } from '../../../api/gen/model/parkingProcessListItemDto';
import {
    JjamesButtonComponent,
    JJamesCardComponent,
    JJamesAlertComponent,
    JJamesTableComponent,
    JJamesPaginatorComponent,
    JJamesTableColumn,
    JJamesPaginationConfig
} from 'shared-lib';

@Component({
    selector: 'app-pool-config-transactions',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        JjamesButtonComponent,
        JJamesCardComponent,
        JJamesAlertComponent,
        JJamesTableComponent,
        JJamesPaginatorComponent
    ],
    templateUrl: './pool-config-transactions.component.html',
    styleUrls: ['./pool-config-transactions.component.scss']
})
export class PoolConfigTransactionsComponent implements OnInit {
    private readonly parkingProcessService = inject(ParkingProcessService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly fb = inject(FormBuilder);

    orgId = signal<string | null>(null);
    configId = signal<string | null>(null);

    loading = signal(false);
    errorMessage = signal<string | null>(null);

    transactions = signal<ParkingProcessListItemDto[]>([]);

    pageIndex = signal(0);
    pageSize = signal(20);
    totalCount = signal(0);

    filterForm!: FormGroup;

    columns: JJamesTableColumn[] = [
        { key: 'startedAt', label: 'Started At', sortable: true },
        { key: 'endedAt', label: 'Ended At' },
        { key: 'plate', label: 'Plate' },
        { key: 'zone', label: 'Parking Zone' },
        { key: 'duration', label: 'Duration' },
    ];

    get paginationConfig(): JJamesPaginationConfig {
        const total = this.totalCount();
        const size = this.pageSize();
        return {
            currentPage: this.pageIndex(),
            totalPages: Math.ceil(total / size),
            pageSize: size,
            totalItems: total,
            itemsPerPage: size,
            pageSizeOptions: [10, 20, 50]
        };
    }

    ngOnInit(): void {
        this.orgId.set(this.route.snapshot.paramMap.get('orgId'));
        this.configId.set(this.route.snapshot.paramMap.get('configId'));

        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        this.filterForm = this.fb.group({
            from: [firstOfMonth.toISOString().substring(0, 10)],
            to: [lastOfMonth.toISOString().substring(0, 10)]
        });

        this.load();
    }

    load(): void {
        if (!this.configId()) return;

        this.loading.set(true);
        this.errorMessage.set(null);

        const { from, to } = this.filterForm.value;

        this.parkingProcessService.parkingProcessFilterEnrichedPost({
            startedFrom: from ? new Date(from).toISOString() : undefined,
            startedTo: to ? new Date(to + 'T23:59:59').toISOString() : undefined,
            poolParkingConfigId: this.configId() ?? undefined,
            pageIndex: this.pageIndex(),
            pageSize: this.pageSize(),
            includeDeleted: false,
            includeValidity: false
        }).subscribe({
            next: (result) => {
                this.transactions.set(result.items || []);
                this.totalCount.set(result.totalCount ?? 0);
                this.loading.set(false);
            },
            error: (err: any) => {
                this.errorMessage.set(err.error?.title || 'Failed to load transactions.');
                this.loading.set(false);
            }
        });
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex.set(event.pageIndex);
        this.pageSize.set(event.pageSize);
        this.load();
    }

    applyFilter(): void {
        this.pageIndex.set(0);
        this.load();
    }

    getTableData(): Record<string, string>[] {
        return this.transactions().map(t => ({
            startedAt: t.startedAt ? new Date(t.startedAt).toLocaleString() : '—',
            endedAt: t.endedAt ? new Date(t.endedAt as string).toLocaleString() : 'Ongoing',
            plate: t.licensePlateDetails?.fullNumber ?? t.licensePlateId ?? '—',
            zone: t.parkingZoneDetails?.name ?? t.parkingZoneId ?? '—',
            duration: t.duration ?? '—',
        }));
    }

    goBack(): void {
        this.router.navigate(['/org-management', this.orgId(), 'pool-config', this.configId()]);
    }
}
